import React, { useEffect } from 'react';
import { MantineProvider, Box } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { mantineTheme } from './presentation/styles/theme/mantineTheme';
import { AppRoutes } from './presentation/routing/AppRoutes';
import { useAuthStore } from './application/store/authStore';
import { NOTIFICATION_CONFIG } from './infrastructure/notifications';
import { ErrorBoundary, ErrorFallback } from './presentation/components/ErrorBoundary';
import { OfflineBanner } from './presentation/components/OfflineBanner';
import { LoadingOverlay } from './presentation/components/common/LoadingOverlay';
import { queryClient } from './application/queries/queryClient';
import { StripeProvider } from './presentation/providers/StripeProvider';

// reCAPTCHA v3 Site Key (public - same as Marketing site)
// SESSION_193: CAPTCHA integration for registration
const RECAPTCHA_SITE_KEY = '6Lfip-srAAAAAI6Hzh13EuhEzPUn_UOfNqONlxM7';

// Import Mantine core styles
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

/**
 * Main App Component
 * Entry point for the MentionedMe application
 */
export const App: React.FC = (): React.ReactElement => {
  const loadCurrentUser = useAuthStore((state) => state.loadCurrentUser);
  const isAuthLoading = useAuthStore((state) => state.isLoading);
  const loadingMessage = useAuthStore((state) => state.loadingMessage);

  // Initialize authentication on app startup
  useEffect(() => {
    void loadCurrentUser();
  }, [loadCurrentUser]);

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <MantineProvider theme={mantineTheme}>
          <GoogleReCaptchaProvider
            reCaptchaKey={RECAPTCHA_SITE_KEY}
            language="en"
            scriptProps={{ async: true, defer: true, appendTo: 'head' }}
          >
          <StripeProvider>
          <ModalsProvider>
            <Notifications
              position={NOTIFICATION_CONFIG.POSITION}
              zIndex={NOTIFICATION_CONFIG.Z_INDEX}
              limit={NOTIFICATION_CONFIG.MAX_NOTIFICATIONS}
            />
            <OfflineBanner />
            <BrowserRouter>
              <ErrorBoundary fallback={ErrorFallback}>
                <Box pos="relative" mih="100vh">
                  <LoadingOverlay visible={isAuthLoading} message={loadingMessage ?? 'Loading...'} />
                  <AppRoutes />
                </Box>
              </ErrorBoundary>
            </BrowserRouter>
          </ModalsProvider>
          </StripeProvider>
          </GoogleReCaptchaProvider>
        </MantineProvider>
        {/* React Query DevTools - only in development */}
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
